import { useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    FormControlLabel,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { Title, useDataProvider, useNotify, useRedirect } from 'react-admin';
import type { CrmDataProvider } from '@/providers/types';
import { useDropzone } from 'react-dropzone';

import { HelpDocumentAside } from './HelpDocumentAside';

export const HelpDocumentCreate = () => {
    const notify = useNotify();
    const redirect = useRedirect();
    const dataProvider = useDataProvider<CrmDataProvider>();
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [isActive, setIsActive] = useState(true);
    const [saving, setSaving] = useState(false);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        accept: {
            'application/pdf': ['.pdf'],
            'application/msword': ['.doc'],
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
                ['.docx'],
            'application/vnd.ms-word.document.macroEnabled.12': ['.docm'],
            'application/vnd.ms-excel': ['.xls'],
            'application/vnd.oasis.opendocument.text': ['.odt'],
            'application/vnd.oasis.opendocument.spreadsheet': ['.ods'],
            'application/vnd.oasis.opendocument.presentation': ['.odp'],
            'text/csv': ['.csv'],
            'application/rtf': ['.rtf'],
            'text/rtf': ['.rtf'],
            'application/epub+zip': ['.epub'],
        },
        multiple: false,
        onDrop: accepted => {
            if (accepted[0]) setFile(accepted[0]);
        },
    });

    const handleSave = async () => {
        if (!title.trim()) {
            notify('Title is required', { type: 'warning' });
            return;
        }
        if (!file && !content.trim()) {
            notify('Paste text or upload a document', { type: 'warning' });
            return;
        }
        setSaving(true);
        try {
            const doc = await dataProvider.createHelpDocument({
                title: title.trim(),
                content: content.trim(),
                is_active: isActive,
                file,
                kind: file ? undefined : 'text',
            });
            notify('Document created', { type: 'info' });
            redirect(`/settings/help-knowledge/${doc.id}`);
        } catch (err) {
            notify(err instanceof Error ? err.message : 'Create failed', {
                type: 'warning',
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box mt={2} display="flex">
            <Title title="New help document" />
            <HelpDocumentAside />
            <Box flex="1" maxWidth={800}>
                <Card>
                    <CardContent>
                        <Stack spacing={2}>
                            <TextField
                                label="Title"
                                value={title}
                                onChange={e => setTitle(e.target.value)}
                                fullWidth
                                required
                            />
                            <Box
                                {...getRootProps()}
                                sx={{
                                    border: '1px dashed',
                                    borderColor: isDragActive
                                        ? 'primary.main'
                                        : 'divider',
                                    borderRadius: 1,
                                    p: 2,
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    bgcolor: 'action.hover',
                                }}
                            >
                                <input {...getInputProps()} />
                                <Typography variant="body2">
                                    {file
                                        ? `File: ${file.name}`
                                        : 'Drop a document here (PDF, Word, Excel, OpenDocument, CSV, RTF, EPUB — converted to markdown on the server), or paste text below'}
                                </Typography>
                            </Box>
                            <TextField
                                label="Markdown / text content"
                                value={content}
                                onChange={e => setContent(e.target.value)}
                                fullWidth
                                multiline
                                minRows={8}
                                maxRows={20}
                                disabled={!!file}
                                helperText={
                                    file
                                        ? 'Content will be extracted from the uploaded file'
                                        : undefined
                                }
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isActive}
                                        onChange={e =>
                                            setIsActive(e.target.checked)
                                        }
                                    />
                                }
                                label="Active (included in agent context)"
                            />
                            <Button
                                variant="contained"
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving ? 'Saving…' : 'Save'}
                            </Button>
                        </Stack>
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
};
